// src/components/ClientToggle.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown } from 'lucide-react';

/**
 * ClientToggle - Development tool to switch between different clients
 * Similar to DevToggle but for client selection
 * 
 * @param {string} currentClientId - Currently active client ID
 * @param {string} actualClientId - The actual client ID from profile/context
 * @param {boolean} isOverridden - Whether client is being overridden
 * @param {function} onClientChange - Callback when client is changed
 * @param {function} onReset - Callback to reset to actual client
 */
export function ClientToggle({ 
  currentClientId, 
  actualClientId, 
  isOverridden, 
  onClientChange, 
  onReset 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Available clients
  const clients = [
    { id: '1', name: 'Baseline Analytics', description: 'Generic baseline' },
    { id: '101', name: 'Demo Client', description: 'Default demo client' },
    { id: '200', name: 'Regional Health System', description: 'Mid-Atlantic system' },
    { id: '300', name: 'North Star Consortium', description: 'Latin America medical tourism' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentClient = clients.find(c => c.id === currentClientId) || clients[0];

  return (
    <div 
      ref={dropdownRef}
      className="fixed bottom-4 left-4 z-[9999] bg-white rounded-lg shadow-lg border-2 border-amber-400 overflow-hidden"
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-amber-600" />
          <div className="text-left">
            <div className="text-xs font-semibold text-slate-700">
              Client: {currentClient.name}
            </div>
            {isOverridden && (
              <div className="text-[10px] text-amber-600 font-medium">
                Override Active
              </div>
            )}
          </div>
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown - Shown when open */}
      {isOpen && (
        <div className="border-t border-slate-200">
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-100">
            <div className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide">
              Select Client
            </div>
          </div>
          
          {/* Client Options */}
          <div className="max-h-64 overflow-y-auto">
            {clients.map(client => (
              <button
                key={client.id}
                onClick={() => {
                  onClientChange(client.id);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 ${
                  client.id === currentClientId ? 'bg-slate-100' : ''
                }`}
              >
                <div className="text-xs font-medium text-slate-800">
                  {client.name}
                  {client.id === currentClientId && (
                    <span className="ml-2 text-[10px] text-emerald-600 font-semibold">
                      ✓ Active
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  ID: {client.id} · {client.description}
                </div>
              </button>
            ))}
          </div>

          {/* Reset Button */}
          {isOverridden && (
            <div className="px-3 py-2 bg-slate-50 border-t border-slate-200">
              <button
                onClick={() => {
                  onReset();
                  setIsOpen(false);
                }}
                className="w-full text-xs text-amber-700 hover:text-amber-800 font-medium"
              >
                Reset to Actual Client ({actualClientId})
              </button>
            </div>
          )}

          {/* Info Footer */}
          <div className="px-3 py-2 bg-amber-50 border-t border-amber-100">
            <div className="text-[10px] text-amber-700">
              <strong>Dev Tool:</strong> Switch clients to test different data contexts
            </div>
          </div>
        </div>
      )}
    </div>
  );
}